import { Anchor, Group, Skeleton, Text } from '@mantine/core';
import { IconCoin } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { BILLING_ENABLED } from '@constants/features';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import useResolveCreditAccount from '@hooks/credit/use-resolve-credit-account';
import useAccountBalances from '@hooks/subscription/use-account-balances';

/** Billable products shown on Doc Pabean (PIB/PEB + Manifest house). */
const DOC_PABEAN_CREDIT_PRODUCTS = [
  { code: 'ceisa_submission', label: 'PIB / PEB' },
  { code: 'nvocc_manifest_submission', label: 'Manifest' },
] as const;

/**
 * Remaining credits strip for Doc Pabean and Doc Manifes. Only renders when
 * the PPJK has credit enforcement on (`isCreditActive` from balances API).
 */
export default function DocPabeanCreditsBanner() {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const { accountCode, loading: resolveLoading, isPaymentActive } = useResolveCreditAccount();
  const {
    data: balances,
    loading: balancesLoading,
    error: balancesError,
    isCreditActive,
  } = useAccountBalances(isPaymentActive ? accountCode : undefined);

  // Wait for resolve + balances so we don't flash "Sisa kredit" for inactive PPJKs.
  if (resolveLoading || (Boolean(accountCode) && balancesLoading)) {
    return (
      <Group gap="md" mb="md" wrap="wrap">
        <Skeleton height={36} width={160} radius="md" />
        <Skeleton height={36} width={160} radius="md" />
      </Group>
    );
  }

  // Gate: linked account alone is not enough — hide when credit is inactive.
  if (!isPaymentActive || !accountCode || !isCreditActive) {
    return null;
  }

  const balanceFor = (productCode: string): number | null => {
    const row = balances.find((b) => b.product?.code === productCode);
    return row ? row.totalCredits : null;
  };

  return (
    <Group
      data-tour="docpabean-credits"
      justify="space-between"
      align="center"
      wrap="wrap"
      mb="md"
      gap="sm"
      p="sm"
      style={{
        backgroundColor: `${themeColors.primary}08`,
        border: `1px solid ${themeColors.border}`,
        borderRadius: 8,
      }}
    >
      <Group gap="md" wrap="wrap">
        <Group gap={6}>
          <IconCoin size={16} style={{ color: themeColors.primary }} />
          <Text size="sm" fw={600} style={{ color: themeColors.text }}>
            {t('docpabean.remainingCredits')}
          </Text>
        </Group>

        {DOC_PABEAN_CREDIT_PRODUCTS.map(({ code, label }) => {
          const balance = balanceFor(code);
          return (
            <Group
              key={code}
              gap={6}
              px="sm"
              py={4}
              style={{
                backgroundColor: themeColors.card,
                border: `1px solid ${themeColors.border}`,
                borderRadius: 6,
              }}
            >
              <Text size="xs" style={{ color: themeColors.textSecondary }}>
                {label}
              </Text>
              <Text size="sm" fw={700} style={{ color: themeColors.text }}>
                {balance !== null ? balance : '—'}
              </Text>
            </Group>
          );
        })}

        {balancesError && (
          <Text size="xs" c="red">
            {t('docpabean.errLoadBalance')}
          </Text>
        )}
      </Group>

      {BILLING_ENABLED && (
        <Anchor component={Link} to="/subscription" size="xs" style={{ color: themeColors.primary }}>
          {t('docpabean.viewSubscription')}
        </Anchor>
      )}
    </Group>
  );
}
